/**
 * filter-bar.js
 *
 * Manages dropdown open/close, Escape + outside-click dismissal, and the
 * no-reload filter/sort interaction layer: intercepts anchor clicks on
 * [data-filter-checkbox] and [data-sort-link], does a pushState + fetch,
 * then swaps [data-filter-bar], [data-active-chips], [data-product-count]
 * and [data-product-grid]
 * from the fetched HTML. Falls back to full navigation on fetch failure.
 *
 * Price range inputs debounce URL updates by 200 ms.
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 200;

  // Group IDs whose mobile "See more" expander is currently expanded. The set
  // survives the filter XHR innerHTML swap so expanded groups re-expand on the
  // refreshed markup (issue #183 / M3e). Group IDs are the stable dropdown ids
  // (derived from filter.id + title), so they match across re-renders.
  var expandedGroups = {};

  function applyExpandedGroups(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-filter-expandable]').forEach(function (cols) {
      var id = cols.getAttribute('data-filter-group-id');
      var btn = cols.parentElement
        ? cols.parentElement.querySelector('[data-filter-see-more]')
        : null;
      var expanded = id && expandedGroups[id] === true;
      setGroupExpanded(cols, btn, expanded);
    });
  }

  function setGroupExpanded(cols, btn, expanded) {
    if (expanded) {
      cols.setAttribute('data-expanded', '');
    } else {
      cols.removeAttribute('data-expanded');
    }
    if (!btn) return;
    btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    var moreLabel = btn.querySelector('[data-see-more-label]');
    var lessLabel = btn.querySelector('[data-see-less-label]');
    if (moreLabel) {
      if (expanded) {
        moreLabel.setAttribute('hidden', '');
      } else {
        moreLabel.removeAttribute('hidden');
      }
    }
    if (lessLabel) {
      if (expanded) {
        lessLabel.removeAttribute('hidden');
      } else {
        lessLabel.setAttribute('hidden', '');
      }
    }
  }

  // ------------------------------------------------------------------
  // Dropdown helpers
  // ------------------------------------------------------------------

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function panelForBtn(btn) {
    var panelId = btn.getAttribute('aria-controls');
    return panelId ? document.getElementById(panelId) : null;
  }

  // Only the collection filter GROUPS (.filter-bar__filters) get the animated
  // accordion treatment - their panels carry the matching CSS transition. The
  // sort dropdown reuses [data-filter-dropdown-btn] but lives in
  // .filter-bar__sort with no transition, so it must stay an instant toggle:
  // animating it would set max-height with no transitionend to clear the lock.
  function isAccordionBtn(btn) {
    return !!(btn.closest && btn.closest('.filter-bar__filters'));
  }

  // Resolve cb once the panel's own max-height transition ends - mirrors the
  // shared snippets/accordion-disclosure.liquid controller (FAQ + product
  // accordions) so every accordion animates with identical logic.
  function onMaxHeightEnd(panel, cb) {
    function te(e) {
      if (e.target !== panel || e.propertyName !== 'max-height') return;
      panel.removeEventListener('transitionend', te);
      cb();
    }
    panel.addEventListener('transitionend', te);
  }

  // Animated disclosure for the drawer accordion. Reuses accordion-disclosure's
  // exact technique + timing (max-height 0.5s + opacity 0.3s, measured from
  // scrollHeight). The aria-expanded flip drives the plus/minus icon morph.
  function showPanel(btn, panel) {
    btn.setAttribute('aria-expanded', 'true');
    panel.removeAttribute('hidden');
    if (!isDrawerViewport() || !isAccordionBtn(btn) || reduceMotion.matches) return;
    btn.setAttribute('data-animating', '');
    panel.style.maxHeight = '0px';
    panel.style.opacity = '0';
    void panel.offsetHeight; // force reflow so the transition runs
    panel.style.maxHeight = panel.scrollHeight + 'px';
    panel.style.opacity = '1';
    onMaxHeightEnd(panel, function () {
      panel.style.maxHeight = 'none'; // allow See more / responsive resize
      panel.style.opacity = '';
      btn.removeAttribute('data-animating');
    });
  }

  function hidePanel(btn, panel) {
    if (!isDrawerViewport() || !isAccordionBtn(btn) || reduceMotion.matches) {
      btn.setAttribute('aria-expanded', 'false');
      panel.setAttribute('hidden', '');
      return;
    }
    btn.setAttribute('aria-expanded', 'false'); // flips the icon to plus now
    btn.setAttribute('data-animating', '');
    panel.style.maxHeight = panel.scrollHeight + 'px';
    panel.style.opacity = '1';
    void panel.offsetHeight;
    panel.style.maxHeight = '0px';
    panel.style.opacity = '0';
    onMaxHeightEnd(panel, function () {
      panel.setAttribute('hidden', '');
      panel.style.maxHeight = '';
      panel.style.opacity = '';
      btn.removeAttribute('data-animating');
    });
  }

  // Instant close for dismissal/teardown (outside-click, Escape, and the filter
  // XHR which re-renders the markup). Never animate here - it would race the
  // innerHTML swap. Clears any in-flight animation styles too.
  function closeAllDropdowns() {
    document.querySelectorAll('[data-filter-dropdown-btn][aria-expanded="true"]').forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
      btn.removeAttribute('data-animating');
      var panel = panelForBtn(btn);
      if (panel) {
        panel.setAttribute('hidden', '');
        panel.style.maxHeight = '';
        panel.style.opacity = '';
      }
    });
  }

  function openDropdown(btn) {
    var panel = panelForBtn(btn);
    if (!panel) return;
    // Desktop popovers are single-open; the drawer accordion is multi-open
    // (matching the live inline filter layout), so don't close siblings there.
    if (!isDrawerViewport()) {
      closeAllDropdowns();
      btn.setAttribute('aria-expanded', 'true');
      panel.removeAttribute('hidden');
      return;
    }
    showPanel(btn, panel);
  }

  function toggleDropdown(btn) {
    if (btn.hasAttribute('data-animating')) return; // lock during a transition
    var panel = panelForBtn(btn);
    if (!panel) return;
    var isOpen = btn.getAttribute('aria-expanded') === 'true';
    if (!isOpen) {
      openDropdown(btn);
    } else if (isDrawerViewport()) {
      hidePanel(btn, panel);
    } else {
      btn.setAttribute('aria-expanded', 'false');
      panel.setAttribute('hidden', '');
    }
  }

  // ------------------------------------------------------------------
  // Fetch-and-replace
  // ------------------------------------------------------------------

  function swapRegion(selector, newDoc) {
    var current = document.querySelector(selector);
    var fresh = newDoc.querySelector(selector);
    if (current && fresh) current.innerHTML = fresh.innerHTML;
  }

  function fetchAndReplace(href) {
    var bar = document.querySelector('[data-filter-bar]');
    var grid = document.querySelector('[data-product-grid]');
    if (bar) bar.setAttribute('aria-busy', 'true');
    if (grid) grid.setAttribute('aria-busy', 'true');

    fetch(href)
      .then(function (res) {
        if (!res.ok) throw new Error('fetch failed');
        return res.text();
      })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        swapRegion('[data-filter-bar]', doc);
        swapRegion('[data-active-chips]', doc);
        swapRegion('[data-product-count]', doc);
        swapRegion('[data-product-grid]', doc);
        // Re-apply persisted "See more" expand state to the fresh markup so
        // groups the shopper expanded stay open across the filter refresh.
        applyExpandedGroups(document.querySelector('[data-filter-bar]'));
        if (bar) bar.removeAttribute('aria-busy');
        if (grid) grid.removeAttribute('aria-busy');
      })
      .catch(function () {
        window.location.href = href;
      });
  }

  // ------------------------------------------------------------------
  // Event delegation  -  click
  // ------------------------------------------------------------------

  document.addEventListener('click', function (e) {
    // "See more / See less" expander (mobile long-list truncation, #183).
    // Handle before the dropdown toggle so the click inside the open panel
    // doesn't bubble up and collapse the whole group.
    var seeMoreBtn = e.target.closest('[data-filter-see-more]');
    if (seeMoreBtn) {
      e.preventDefault();
      e.stopPropagation();
      var cols = seeMoreBtn.parentElement
        ? seeMoreBtn.parentElement.querySelector('[data-filter-expandable]')
        : null;
      if (!cols) return;
      var groupId = cols.getAttribute('data-filter-group-id');
      var nowExpanded = !cols.hasAttribute('data-expanded');
      if (groupId) {
        if (nowExpanded) {
          expandedGroups[groupId] = true;
        } else {
          delete expandedGroups[groupId];
        }
      }
      setGroupExpanded(cols, seeMoreBtn, nowExpanded);
      return;
    }

    // Dropdown toggle button
    var btn = e.target.closest('[data-filter-dropdown-btn]');
    if (btn) {
      e.stopPropagation();
      toggleDropdown(btn);
      return;
    }

    // Filter checkbox / chip / clear-all links
    var filterLink = e.target.closest('[data-filter-checkbox]');
    if (filterLink) {
      e.preventDefault();
      var href = filterLink.getAttribute('href');
      if (!href) return;
      window.history.pushState(null, '', href);
      closeAllDropdowns();
      fetchAndReplace(href);
      return;
    }

    // Sort option links
    var sortLink = e.target.closest('[data-sort-link]');
    if (sortLink) {
      e.preventDefault();
      var url = new URL(window.location.href);
      var sortVal = sortLink.dataset.sortValue;
      if (sortVal) {
        url.searchParams.set('sort_by', sortVal);
      } else {
        url.searchParams.delete('sort_by');
      }
      url.searchParams.set('page', '1');
      var sortHref = url.pathname + (url.search || '');
      window.history.pushState(null, '', sortHref);
      closeAllDropdowns();
      fetchAndReplace(sortHref);
      return;
    }

    // Close on outside click
    if (!e.target.closest('[data-filter-dropdown]')) {
      closeAllDropdowns();
    }
  });

  // ------------------------------------------------------------------
  // Event delegation  -  keydown
  // ------------------------------------------------------------------

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var openBtn = document.querySelector('[data-filter-dropdown-btn][aria-expanded="true"]');
    closeAllDropdowns();
    if (openBtn) openBtn.focus();
  });

  // ------------------------------------------------------------------
  // Price range  -  event delegation on input
  // ------------------------------------------------------------------

  var priceTimer = null;

  function updatePriceFill(minInput, maxInput) {
    var fill = minInput.closest('[data-price-filter]');
    fill = fill ? fill.querySelector('[data-price-fill]') : null;
    if (!fill) return;
    var rangeMin = parseFloat(minInput.dataset.rangeMin) || 0;
    var rangeMax = parseFloat(minInput.dataset.rangeMax) || 100;
    var span = rangeMax - rangeMin;
    if (span === 0) return;
    var minPct = ((parseFloat(minInput.value) - rangeMin) / span) * 100;
    var maxPct = ((parseFloat(maxInput.value) - rangeMin) / span) * 100;
    fill.style.left = minPct + '%';
    fill.style.width = (maxPct - minPct) + '%';
  }

  function updatePriceDisplay(minInput, maxInput) {
    var wrap = minInput.closest('[data-price-filter]');
    if (!wrap) return;
    var displayMin = wrap.querySelector('[data-price-display-min]');
    var displayMax = wrap.querySelector('[data-price-display-max]');
    if (displayMin) displayMin.textContent = '$' + Math.floor(parseFloat(minInput.value));
    if (displayMax) displayMax.textContent = '$' + Math.floor(parseFloat(maxInput.value));
  }

  document.addEventListener('input', function (e) {
    var isMin = e.target.matches('[data-price-min-input]');
    var isMax = e.target.matches('[data-price-max-input]');
    if (!isMin && !isMax) return;

    var wrap = e.target.closest('[data-price-filter]');
    if (!wrap) return;
    var minInput = wrap.querySelector('[data-price-min-input]');
    var maxInput = wrap.querySelector('[data-price-max-input]');
    if (!minInput || !maxInput) return;

    var minVal = parseFloat(minInput.value);
    var maxVal = parseFloat(maxInput.value);

    if (isMin && minVal > maxVal) {
      minInput.value = maxVal;
      minVal = maxVal;
    }
    if (isMax && maxVal < minVal) {
      maxInput.value = minVal;
      maxVal = minVal;
    }

    updatePriceDisplay(minInput, maxInput);
    updatePriceFill(minInput, maxInput);

    clearTimeout(priceTimer);
    priceTimer = setTimeout(function () {
      var url = new URL(window.location.href);
      url.searchParams.set('filter.v.price.gte', minVal);
      url.searchParams.set('filter.v.price.lte', maxVal);
      url.searchParams.set('page', '1');
      var href = url.pathname + url.search;
      window.history.pushState(null, '', href);
      fetchAndReplace(href);
    }, DEBOUNCE_MS);
  });

  // Price reset button
  document.addEventListener('click', function (e) {
    if (!e.target.matches('[data-price-reset]') && !e.target.closest('[data-price-reset]')) return;
    e.preventDefault();
    var url = new URL(window.location.href);
    url.searchParams.delete('filter.v.price.gte');
    url.searchParams.delete('filter.v.price.lte');
    url.searchParams.set('page', '1');
    var href = url.pathname + (url.search || '');
    window.history.pushState(null, '', href);
    closeAllDropdowns();
    fetchAndReplace(href);
  });

  // ------------------------------------------------------------------
  // Init price fill on load
  // ------------------------------------------------------------------

  document.addEventListener('DOMContentLoaded', function () {
    applyExpandedGroups(document);
    document.querySelectorAll('[data-price-filter]').forEach(function (wrap) {
      var minInput = wrap.querySelector('[data-price-min-input]');
      var maxInput = wrap.querySelector('[data-price-max-input]');
      if (minInput && maxInput) {
        updatePriceFill(minInput, maxInput);
      }
    });
  });

  // ------------------------------------------------------------------
  // Filter drawer (mobile) / inline reveal (desktop) -- issue #164
  //
  // The "Filter & Sort" trigger reveals [data-filter-bar]. On desktop that
  // is the inline bar (unchanged). On mobile (<=768px) CSS presents the same
  // region as a slide-in drawer; here we add the modal behaviours that CSS
  // cannot: backdrop, body scroll-lock, focus trap, Escape + close button.
  // Modal extras engage only at the mobile breakpoint so desktop stays a
  // plain reveal.
  // ------------------------------------------------------------------

  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';
  var drawerTrigger = null;
  // Portal restore points. The drawer + backdrop move to <body> while open so
  // they escape the .collection-header stacking context (isolation:isolate +
  // z-index:2), which otherwise traps the drawer below the sticky site header.
  var portalAnchor = null;
  var portalParent = null;

  function portalToBody() {
    var panel = document.querySelector('[data-filter-bar]');
    var backdrop = document.querySelector('[data-filter-backdrop]');
    if (!panel || panel.parentElement === document.body) return;
    portalParent = panel.parentElement;
    portalAnchor = document.createComment('filter-drawer-portal');
    portalParent.insertBefore(portalAnchor, backdrop || panel);
    if (backdrop) document.body.appendChild(backdrop);
    document.body.appendChild(panel);
  }

  function restoreFromBody() {
    if (!portalAnchor || !portalParent) return;
    var panel = document.querySelector('[data-filter-bar]');
    var backdrop = document.querySelector('[data-filter-backdrop]');
    if (backdrop) portalParent.insertBefore(backdrop, portalAnchor);
    if (panel) portalParent.insertBefore(panel, portalAnchor);
    portalParent.removeChild(portalAnchor);
    portalAnchor = null;
    portalParent = null;
  }

  function isDrawerViewport() {
    return window.matchMedia('(max-width: 768px)').matches;
  }

  function drawerEls() {
    return {
      panel: document.querySelector('[data-filter-bar]'),
      backdrop: document.querySelector('[data-filter-backdrop]'),
      trigger: document.querySelector('[data-filter-trigger]')
    };
  }

  function trapFocus(e) {
    if (e.key !== 'Tab') return;
    var panel = document.querySelector('[data-filter-bar]');
    if (!panel) return;
    var nodes = Array.prototype.slice.call(panel.querySelectorAll(FOCUSABLE)).filter(function (n) {
      return n.offsetParent !== null;
    });
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // Slide-out duration must stay in lockstep with the `.filter-drawer`
  // transition in filter-drawer.liquid (0.2s) -- it is the fallback timer for
  // the transitionend that triggers hide + un-portal on close.
  var DRAWER_ANIM_MS = 220;

  function openDrawer() {
    var els = drawerEls();
    if (!els.panel) return;
    drawerTrigger = els.trigger || null;
    els.panel.removeAttribute('hidden');
    if (els.trigger) els.trigger.setAttribute('aria-expanded', 'true');

    if (isDrawerViewport()) {
      portalToBody();
      if (els.backdrop) els.backdrop.removeAttribute('hidden');
      els.panel.setAttribute('aria-modal', 'true');
      document.addEventListener('keydown', trapFocus, true);
      // Defer the open-state classes by two frames so the backdrop opacity and
      // panel transform animate FROM their closed values. Toggling them in the
      // same tick as removing [hidden] makes the drawer jump open with no
      // transition (display:none -> visible can't be interpolated).
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          document.documentElement.classList.add('filter-drawer-open');
          els.panel.classList.add('is-open');
        });
      });
      var closeBtn = els.panel.querySelector('[data-filter-close]');
      if (closeBtn) closeBtn.focus();
    } else {
      // Desktop inline reveal -- no slide animation, show immediately.
      els.panel.classList.add('is-open');
    }
  }

  function closeDrawer() {
    var els = drawerEls();
    if (!els.panel) return;
    var animateOut = isDrawerViewport() && els.panel.classList.contains('is-open');

    els.panel.classList.remove('is-open');
    els.panel.setAttribute('aria-modal', 'false');
    if (els.trigger) els.trigger.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('filter-drawer-open');
    document.removeEventListener('keydown', trapFocus, true);

    function finalize() {
      els.panel.setAttribute('hidden', '');
      if (els.backdrop) els.backdrop.setAttribute('hidden', '');
      restoreFromBody();
    }

    if (animateOut) {
      // Hide + un-portal only after the slide-out + backdrop fade complete,
      // so the drawer animates closed instead of vanishing.
      var done = false;
      var onEnd = function (e) {
        if (e.target !== els.panel || e.propertyName !== 'transform') return;
        done = true;
        els.panel.removeEventListener('transitionend', onEnd);
        finalize();
      };
      els.panel.addEventListener('transitionend', onEnd);
      window.setTimeout(function () {
        if (done) return;
        els.panel.removeEventListener('transitionend', onEnd);
        finalize();
      }, DRAWER_ANIM_MS + 80);
    } else {
      finalize();
    }

    if (drawerTrigger) {
      drawerTrigger.focus();
      drawerTrigger = null;
    }
  }

  function drawerIsOpen() {
    var panel = document.querySelector('[data-filter-bar]');
    return panel && !panel.hasAttribute('hidden');
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-filter-trigger]')) {
      e.preventDefault();
      if (drawerIsOpen()) {
        closeDrawer();
      } else {
        openDrawer();
      }
      return;
    }
    if (e.target.closest('[data-filter-close]') || e.target.matches('[data-filter-backdrop]')) {
      e.preventDefault();
      closeDrawer();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && drawerIsOpen() && isDrawerViewport()) {
      closeDrawer();
    }
  });

})();
